//#region common/barcode.ts
/**
* Barcode types supported across the platform, plus per-type validation and
* auto-generation (GS1 restricted range) utilities.
*
* GTIN/UPC/EAN/ISBN-13 all share the GS1 mod-10 check digit — the validators
* below enforce digit length and the check character. ASIN has no checksum, so
* only the shape is validated. CUSTOM accepts any non-empty string.
*/
const BARCODE_TYPES = [
	"GTIN",
	"UPC",
	"EAN",
	"ISBN",
	"ASIN",
	"CUSTOM"
];
/** GS1's restricted-distribution range (200–299) — for internal-use codes. */
const RESTRICTED_RANGE_PREFIX_MIN = 200;
const RESTRICTED_RANGE_PREFIX_MAX = 299;
const digitsOnly = (code) => /^\d+$/.test(code);
/**
* GS1 mod-10 check digit: weights alternate 3,1,3,... counting from the
* RIGHT over the payload digits; the check digit completes the sum to the
* next multiple of 10. Works for GTIN-8/12/13/14 payloads alike.
*/
const computeGtinCheckDigit = (payload) => {
	if (!digitsOnly(payload)) return "";
	let sum = 0;
	const reversed = payload.split("").reverse();
	for (let i = 0; i < reversed.length; i++) sum += Number(reversed[i]) * (i % 2 === 0 ? 3 : 1);
	return String((10 - sum % 10) % 10);
};
const isValidGtinCheckDigit = (code) => code.endsWith(computeGtinCheckDigit(code.slice(0, -1)));
/** Validates `code` against `type`. Returns an error message when invalid. */
const validateBarcode = (code, type) => {
	const value = (code ?? "").trim();
	if (!value) return;
	switch (type) {
		case "GTIN":
			if (!digitsOnly(value) || ![
				8,
				12,
				13,
				14
			].includes(value.length)) return "GTIN must be 8, 12, 13 or 14 digits";
			if (!isValidGtinCheckDigit(value)) return "GTIN check digit is invalid";
			return;
		case "UPC":
			if (!digitsOnly(value) || value.length !== 12) return "UPC must be exactly 12 digits";
			if (!isValidGtinCheckDigit(value)) return "UPC check digit is invalid";
			return;
		case "EAN":
			if (!digitsOnly(value) || ![8, 13].includes(value.length)) return "EAN must be 8 or 13 digits";
			if (!isValidGtinCheckDigit(value)) return "EAN check digit is invalid";
			return;
		case "ISBN": {
			const isbn10 = /^(?:\d{9}[\dXx])$/.test(value);
			const isbn13 = /^97[89]\d{10}$/.test(value);
			if (!isbn10 && !isbn13) return "ISBN must be a valid ISBN-10 or ISBN-13 (starting 978/979)";
			if (isbn13 && !isValidGtinCheckDigit(value)) return "ISBN-13 check digit is invalid";
			if (isbn10) {
				let sum = 0;
				for (let i = 0; i < 10; i++) {
					const ch = value[i];
					const digit = ch.toUpperCase() === "X" ? 10 : Number(ch);
					sum += digit * (10 - i);
				}
				if (sum % 11 !== 0) return "ISBN-10 check digit is invalid";
			}
			return;
		}
		case "ASIN":
			if (!/^[A-Z0-9]{10}$/.test(value.toUpperCase())) return "ASIN must be 10 letters or digits";
			return;
		case "CUSTOM":
			if (value.length > 64) return "Custom barcode must be at most 64 characters";
			return;
		default:
			if (value.length > 64) return "Barcode must be at most 64 characters";
			return;
	}
};
/**
* Generates a checksum-valid EAN-13 in GS1's restricted-distribution range
* (200–299 prefix). Intended for auto-prefill so brands can use codes
* immediately without a GS1 license; global uniqueness is not guaranteed.
*/
const generateRestrictedEan13 = () => {
	let payload = String(RESTRICTED_RANGE_PREFIX_MIN + Math.floor(Math.random() * (RESTRICTED_RANGE_PREFIX_MAX - RESTRICTED_RANGE_PREFIX_MIN + 1)));
	while (payload.length < 12) payload += String(Math.floor(Math.random() * 10));
	return payload + computeGtinCheckDigit(payload);
};
/**
* Restricted UPC-A: "02" + 9 digits + check digit. UPC-A is EAN-13 with an
* implicit leading zero, so "0 2xx…" falls in GS1's restricted range too.
*/
const generateRestrictedUpc12 = () => {
	let payload = "02";
	while (payload.length < 11) payload += String(Math.floor(Math.random() * 10));
	return payload + computeGtinCheckDigit(payload);
};
/**
* Auto-generation per type. GTIN/UPC/EAN come from GS1's restricted range —
* scannable and checksum-valid, no registry needed. ISBN/ASIN/CUSTOM have no
* honest internal generator (ISBN/ASIN ranges belong to real registries), so
* they return null and the user supplies their own code.
*/
const generateBarcodeForType = (type) => {
	switch (type) {
		case "GTIN":
		case "EAN": return generateRestrictedEan13();
		case "UPC": return generateRestrictedUpc12();
		default: return null;
	}
};

//#endregion
//#region common/enums.ts
let MenuType = /* @__PURE__ */ function(MenuType) {
	MenuType["DINE_IN"] = "DINE_IN";
	MenuType["PICK_UP"] = "PICK_UP";
	MenuType["DELIVERY"] = "DELIVERY";
	return MenuType;
}({});
let SectionType = /* @__PURE__ */ function(SectionType) {
	SectionType["STANDARD"] = "STANDARD";
	SectionType["FEATURED"] = "FEATURED";
	return SectionType;
}({});
let DiscountType = /* @__PURE__ */ function(DiscountType) {
	DiscountType["FLAT"] = "FLAT";
	DiscountType["PERCENTAGE"] = "PERCENTAGE";
	return DiscountType;
}({});
let DisplayType = /* @__PURE__ */ function(DisplayType) {
	DisplayType["EXPANDED"] = "EXPANDED";
	DisplayType["COLLAPSED"] = "COLLAPSED";
	return DisplayType;
}({});
let UserRole = /* @__PURE__ */ function(UserRole) {
	UserRole["ADMIN"] = "ADMIN";
	UserRole["CUSTOMER"] = "CUSTOMER";
	return UserRole;
}({});
let UserTenant = /* @__PURE__ */ function(UserTenant) {
	UserTenant["AITISAL"] = "AITISAL";
	UserTenant["RESTAURANT"] = "RESTAURANT";
	UserTenant["CUSTOMER"] = "CUSTOMER";
	return UserTenant;
}({});
let NestedItemType = /* @__PURE__ */ function(NestedItemType) {
	NestedItemType["PAIRED"] = "PAIRED";
	NestedItemType["BUNDLED"] = "BUNDLED";
	return NestedItemType;
}({});
let SalesChannelGroupType = /* @__PURE__ */ function(SalesChannelGroupType) {
	SalesChannelGroupType["FLOOR"] = "FLOOR";
	SalesChannelGroupType["SYSTEM"] = "SYSTEM";
	return SalesChannelGroupType;
}({});
let SalesChannelType = /* @__PURE__ */ function(SalesChannelType) {
	SalesChannelType["TABLE"] = "TABLE";
	SalesChannelType["WEB"] = "WEB";
	SalesChannelType["POS"] = "POS";
	SalesChannelType["DINE_IN"] = "DINE_IN";
	SalesChannelType["FOODPANDA"] = "FOODPANDA";
	return SalesChannelType;
}({});
const ThirdPartySalesChannels = { FOODPANDA: SalesChannelType.FOODPANDA };
const isFirstPartySalesChannel = (salesChannelType) => !Object.values(ThirdPartySalesChannels).includes(salesChannelType);
let OrderStatus = /* @__PURE__ */ function(OrderStatus) {
	OrderStatus["NEW"] = "NEW";
	OrderStatus["ACCEPTED"] = "ACCEPTED";
	OrderStatus["CLOSED"] = "CLOSED";
	OrderStatus["CANCELLED"] = "CANCELLED";
	return OrderStatus;
}({});
let BranchType = /* @__PURE__ */ function(BranchType) {
	BranchType["RESTAURANT"] = "RESTAURANT";
	BranchType["RETAIL"] = "RETAIL";
	return BranchType;
}({});
const CurrencyCodes = { PKR: "PKR" };
const TimeZones = { "Asia/Karachi": "Asia/Karachi" };
let WeekDay = /* @__PURE__ */ function(WeekDay) {
	WeekDay["Monday"] = "Monday";
	WeekDay["Tuesday"] = "Tuesday";
	WeekDay["Wednesday"] = "Wednesday";
	WeekDay["Thursday"] = "Thursday";
	WeekDay["Friday"] = "Friday";
	WeekDay["Saturday"] = "Saturday";
	WeekDay["Sunday"] = "Sunday";
	return WeekDay;
}({});
let TaxType = /* @__PURE__ */ function(TaxType) {
	TaxType["FLAT"] = "FLAT";
	TaxType["PERCENT"] = "PERCENT";
	return TaxType;
}({});
let BackOrderStatus = /* @__PURE__ */ function(BackOrderStatus) {
	BackOrderStatus["ALLOWED"] = "ALLOWED";
	BackOrderStatus["NOT_ALLOWED"] = "NOT_ALLOWED";
	BackOrderStatus["ALLOWED_WITH_NOTIFICATION"] = "ALLOWED_WITH_NOTIFICATION";
	return BackOrderStatus;
}({});
const CountryCodes = { PK: "PK" };
const CityCodes = {
	KARACHI: "Karachi",
	LAHORE: "Lahore",
	ISLAMABAD: "Islamabad",
	RAWALPINDI: "Rawalpindi",
	FAISALABAD: "Faisalabad",
	MULTAN: "Multan",
	PESHAWAR: "Peshawar",
	QUETTA: "Quetta",
	HYDERABAD: "Hyderabad",
	SIALKOT: "Sialkot",
	GUJRANWALA: "Gujranwala",
	BAHAWALPUR: "Bahawalpur",
	SUKKUR: "Sukkur",
	ABBOTTABAD: "Abbottabad",
	MARDAN: "Mardan",
	SAHIWAL: "Sahiwal"
};
const CitiesByCountry = { PK: [
	"Karachi",
	"Lahore",
	"Islamabad",
	"Rawalpindi",
	"Faisalabad",
	"Multan",
	"Peshawar",
	"Quetta",
	"Hyderabad",
	"Sialkot",
	"Gujranwala",
	"Bahawalpur",
	"Sukkur",
	"Abbottabad",
	"Mardan",
	"Sahiwal"
] };
const LanguageCodes = {
	en: "en",
	ur: "ur"
};
let Action = /* @__PURE__ */ function(Action) {
	Action["Manage"] = "manage";
	Action["Create"] = "create";
	Action["Read"] = "read";
	Action["Update"] = "update";
	Action["Delete"] = "delete";
	return Action;
}({});
let Subject = /* @__PURE__ */ function(Subject) {
	Subject["All"] = "all";
	Subject["Customer"] = "customer";
	Subject["Customization"] = "customization";
	Subject["Item"] = "item";
	Subject["Menu"] = "menu";
	Subject["Order"] = "order";
	Subject["OrderItem"] = "order-item";
	Subject["PointOfOrder"] = "point-of-order";
	Subject["PriceCatalog"] = "price-catalog";
	Subject["QR"] = "qr";
	Subject["Restaurant"] = "restaurant";
	Subject["Section"] = "section";
	Subject["Inventory"] = "inventory";
	Subject["SubOrder"] = "sub-order";
	Subject["POS"] = "pos";
	Subject["Report"] = "report";
	Subject["Table"] = "table";
	return Subject;
}({});
let FulfillmentMethod = /* @__PURE__ */ function(FulfillmentMethod) {
	FulfillmentMethod["BYKEA"] = "BYKEA";
	FulfillmentMethod["BRANCH_DELIVERY"] = "BRANCH_DELIVERY";
	FulfillmentMethod["TAKE_AWAY"] = "TAKE_AWAY";
	FulfillmentMethod["DINE_IN"] = "DINE_IN";
	FulfillmentMethod["PICK_UP"] = "PICK_UP";
	return FulfillmentMethod;
}({});
let LogisticsProvider = /* @__PURE__ */ function(LogisticsProvider) {
	LogisticsProvider["BYKEA"] = "BYKEA";
	return LogisticsProvider;
}({});
let LogisticsOrderStatus = /* @__PURE__ */ function(LogisticsOrderStatus) {
	LogisticsOrderStatus["CREATED"] = "created";
	LogisticsOrderStatus["CANCELLED"] = "cancelled";
	LogisticsOrderStatus["PENDING"] = "pending";
	LogisticsOrderStatus["ACCEPTED"] = "accepted";
	LogisticsOrderStatus["ARRIVED"] = "arrived";
	LogisticsOrderStatus["STARTED"] = "started";
	LogisticsOrderStatus["FINISHED"] = "finished";
	LogisticsOrderStatus["FEEDBACK"] = "feedback";
	return LogisticsOrderStatus;
}({});
let TickerType = /* @__PURE__ */ function(TickerType) {
	TickerType["INFO"] = "info";
	TickerType["WARNING"] = "warning";
	TickerType["SUCCESS"] = "success";
	TickerType["ANNOUNCEMENT"] = "announcement";
	return TickerType;
}({});

//#endregion
//#region common/payment-method.ts
let PaymentMethodType = /* @__PURE__ */ function(PaymentMethodType) {
	PaymentMethodType["CASH"] = "CASH";
	PaymentMethodType["CARD"] = "CARD";
	PaymentMethodType["ONLINE"] = "ONLINE";
	PaymentMethodType["BANK_TRANSFER"] = "BANK_TRANSFER";
	return PaymentMethodType;
}({});
let PaymentProvider = /* @__PURE__ */ function(PaymentProvider) {
	PaymentProvider["SAFEPAY"] = "SAFEPAY";
	return PaymentProvider;
}({});

//#endregion
//#region common/policy.ts
function permissionDocumentToPermissions(permissionDocument) {
	return permissionDocument.sections.flatMap((section) => section.subjectPermissions.map((permission) => ({
		subject: permission.subject,
		action: permission.actions.map((action) => action.allowed ? action.action : null).filter(Boolean)
	})));
}

//#endregion
//#region common/schedule.ts
/**
* Default schedule with all days active from 00:00 to 23:59
*/
const DEFAULT_SCHEDULE = [
	{
		dayOfWeek: WeekDay.Monday,
		isActive: true,
		slots: [{
			startTime: "00:00",
			endTime: "23:59"
		}]
	},
	{
		dayOfWeek: WeekDay.Tuesday,
		isActive: true,
		slots: [{
			startTime: "00:00",
			endTime: "23:59"
		}]
	},
	{
		dayOfWeek: WeekDay.Wednesday,
		isActive: true,
		slots: [{
			startTime: "00:00",
			endTime: "23:59"
		}]
	},
	{
		dayOfWeek: WeekDay.Thursday,
		isActive: true,
		slots: [{
			startTime: "00:00",
			endTime: "23:59"
		}]
	},
	{
		dayOfWeek: WeekDay.Friday,
		isActive: true,
		slots: [{
			startTime: "00:00",
			endTime: "23:59"
		}]
	},
	{
		dayOfWeek: WeekDay.Saturday,
		isActive: true,
		slots: [{
			startTime: "00:00",
			endTime: "23:59"
		}]
	},
	{
		dayOfWeek: WeekDay.Sunday,
		isActive: true,
		slots: [{
			startTime: "00:00",
			endTime: "23:59"
		}]
	}
];
/**
* Order of days in a week (for iteration purposes)
*/
const WEEK_DAYS_ORDER = [
	WeekDay.Monday,
	WeekDay.Tuesday,
	WeekDay.Wednesday,
	WeekDay.Thursday,
	WeekDay.Friday,
	WeekDay.Saturday,
	WeekDay.Sunday
];

//#endregion
//#region common/schedule-utils.ts
/**
* Parse time string (HH:mm) into minutes since midnight
*/
function timeToMinutes(time) {
	const [hours, minutes] = time.split(":").map(Number);
	return hours * 60 + minutes;
}
/**
* Convert minutes since midnight to time string (HH:mm)
*/
function minutesToTime(minutes) {
	const hours = Math.floor(minutes / 60);
	const mins = minutes % 60;
	return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
}
/**
* Get the next day of the week
*/
function getNextDay(day) {
	return WEEK_DAYS_ORDER[(WEEK_DAYS_ORDER.indexOf(day) + 1) % 7];
}
/**
* Check if a time slot crosses day boundary (endTime < startTime)
*/
function slotCrossesDayBoundary(slot) {
	return timeToMinutes(slot.endTime) < timeToMinutes(slot.startTime);
}
/**
* Split a slot that crosses day boundary into two slots
* Returns the remainder slot for the next day
*/
function splitDayCrossingSlot(slot) {
	return {
		currentDaySlot: {
			startTime: slot.startTime,
			endTime: "23:59"
		},
		nextDaySlot: {
			startTime: "00:00",
			endTime: slot.endTime
		}
	};
}
/**
* Check if a time (in minutes) falls within a time slot
*/
function isTimeInSlot(timeMinutes, slot) {
	const startMinutes = timeToMinutes(slot.startTime);
	const endMinutes = timeToMinutes(slot.endTime);
	return timeMinutes >= startMinutes && timeMinutes <= endMinutes;
}
/**
* Check if the current time falls within a schedule
* @param schedule The schedule to check against
* @param timezone The timezone to use (e.g., "Asia/Karachi")
* @param currentDate Optional date to check (defaults to now)
*/
function isWithinSchedule(schedule, timezone, currentDate) {
	if (!schedule || schedule.length === 0) return true;
	const normalized = normalizeSchedule(schedule);
	const now = currentDate || /* @__PURE__ */ new Date();
	let dayOfWeek;
	let currentTimeMinutes;
	try {
		const parts = new Intl.DateTimeFormat("en-US", {
			timeZone: timezone,
			weekday: "long",
			hour: "2-digit",
			minute: "2-digit",
			hour12: false
		}).formatToParts(now);
		dayOfWeek = parts.find((p) => p.type === "weekday")?.value || "";
		let hour = parts.find((p) => p.type === "hour")?.value || "00";
		const minute = parts.find((p) => p.type === "minute")?.value || "00";
		if (hour === "24") hour = "00";
		currentTimeMinutes = parseInt(hour) * 60 + parseInt(minute);
	} catch (error) {
		console.error("Invalid timezone:", timezone, error);
		return true;
	}
	const todaySchedule = normalized.find((s) => s.dayOfWeek === dayOfWeek);
	if (!todaySchedule) return false;
	if (!todaySchedule.isActive) return false;
	if (!todaySchedule.slots || !Array.isArray(todaySchedule.slots) || todaySchedule.slots.length === 0) return true;
	for (const slot of todaySchedule.slots) {
		if (!slot || !slot.startTime || !slot.endTime) continue;
		if (isTimeInSlot(currentTimeMinutes, slot)) return true;
	}
	return false;
}
/**
* Validate a schedule for correctness
*/
function validateSchedule(schedule) {
	const errors = [];
	const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
	const seenDays = /* @__PURE__ */ new Set();
	for (const daySchedule of schedule) {
		if (seenDays.has(daySchedule.dayOfWeek)) errors.push(`Duplicate day of week: ${daySchedule.dayOfWeek}`);
		seenDays.add(daySchedule.dayOfWeek);
		if (!daySchedule.isActive) continue;
		for (let i = 0; i < daySchedule.slots.length; i++) {
			const slot = daySchedule.slots[i];
			if (!timeRegex.test(slot.startTime)) errors.push(`Invalid start time format for ${daySchedule.dayOfWeek} slot ${i + 1}`);
			if (!timeRegex.test(slot.endTime)) errors.push(`Invalid end time format for ${daySchedule.dayOfWeek} slot ${i + 1}`);
		}
	}
	const normalized = normalizeSchedule(schedule);
	for (const daySchedule of normalized) {
		if (!daySchedule.isActive) continue;
		for (let i = 0; i < daySchedule.slots.length; i++) for (let j = i + 1; j < daySchedule.slots.length; j++) if (slotsOverlap(daySchedule.slots[i], daySchedule.slots[j])) errors.push(`Overlapping slots on ${daySchedule.dayOfWeek}: slot ${i + 1} and slot ${j + 1}`);
	}
	return {
		valid: errors.length === 0,
		errors
	};
}
/**
* Check if two time slots overlap
*/
function slotsOverlap(slot1, slot2) {
	const start1 = timeToMinutes(slot1.startTime);
	const end1 = timeToMinutes(slot1.endTime);
	const start2 = timeToMinutes(slot2.startTime);
	return start1 <= timeToMinutes(slot2.endTime) && start2 <= end1;
}
/**
* Normalize a schedule by splitting any slots that cross day boundaries
*/
function normalizeSchedule(schedule) {
	const normalizedSchedule = [];
	for (const daySchedule of schedule) {
		const normalizedSlots = [];
		let nextDaySlots = [];
		for (const slot of daySchedule.slots) if (slotCrossesDayBoundary(slot)) {
			const { currentDaySlot, nextDaySlot } = splitDayCrossingSlot(slot);
			normalizedSlots.push(currentDaySlot);
			nextDaySlots.push(nextDaySlot);
		} else normalizedSlots.push(slot);
		normalizedSchedule.push({
			...daySchedule,
			slots: normalizedSlots
		});
		if (nextDaySlots.length > 0) {
			const nextDay = getNextDay(daySchedule.dayOfWeek);
			const existingNextDay = normalizedSchedule.find((s) => s.dayOfWeek === nextDay);
			if (existingNextDay) existingNextDay.slots = [...nextDaySlots, ...existingNextDay.slots];
			else normalizedSchedule.push({
				dayOfWeek: nextDay,
				isActive: true,
				slots: nextDaySlots
			});
		}
	}
	return normalizedSchedule;
}

//#endregion
export { Action, BARCODE_TYPES, BackOrderStatus, BranchType, CitiesByCountry, CityCodes, CountryCodes, CurrencyCodes, DEFAULT_SCHEDULE, DiscountType, DisplayType, FulfillmentMethod, LanguageCodes, LogisticsOrderStatus, LogisticsProvider, MenuType, NestedItemType, OrderStatus, PaymentMethodType, PaymentProvider, SalesChannelGroupType, SalesChannelType, SectionType, Subject, TaxType, ThirdPartySalesChannels, TickerType, TimeZones, UserRole, UserTenant, WEEK_DAYS_ORDER, WeekDay, computeGtinCheckDigit, generateBarcodeForType, generateRestrictedEan13, generateRestrictedUpc12, getNextDay, isFirstPartySalesChannel, isTimeInSlot, isWithinSchedule, minutesToTime, normalizeSchedule, permissionDocumentToPermissions, slotCrossesDayBoundary, splitDayCrossingSlot, timeToMinutes, validateBarcode, validateSchedule };
//# sourceMappingURL=index.mjs.map